import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import translate from '../../commonComponents/translate';
import withCSRF from '../../commonComponents/withCSRF';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import GameLeadProp from './propTypes/gameLeadProp';
import LeadForm from './leadDetails/LeadForm';
import {
  LEAD_DETAILS_TAB,
  LEAD_STATUS,
  compose,
} from '../../services/utilityFunctions';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import TabbedPortlet from '../../commonComponents/portlets/Tabbed/TabbedPortlet';
import LeadTimeline from './leadDetails/LeadTimeline';
import LeadActions, { LEAD_DETAILS_ACTION } from './leadDetails/LeadActions';
import Request from '../../services/request';
import Notifications from '../../services/notifications';

function LeadDetails({ t, _csrf, countries, lead: initialLead }) {
  const [lead, setLead] = useState(initialLead);
  const { loggedUser } = useContext(LoggedUserContext);

  const fetchFreshLead = () =>
    new Request()
      .get(`/challenges/leads/${lead._id}/data`)
      .done(({ data }) => setLead(data))
      .fail(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchLead');
      });

  const timelineTab = {
    id: LEAD_DETAILS_TAB.TIMELINE,
    title: t('leadDetails_tab_timeline'),
    content: <LeadTimeline lead={lead} />,
  };

  const infoTab = {
    id: LEAD_DETAILS_TAB.INFO,
    title: t('leadDetails_tab_info'),
    content: (
      <div className="m-portlet__body">
        <LeadForm isEdit countries={countries} lead={lead} />
      </div>
    ),
  };

  const notFinished =
    lead.status !== LEAD_STATUS.WON && lead.status !== LEAD_STATUS.LOST;
  const userCanEdit =
    lead.user._id === loggedUser._id ||
    (lead.owner && lead.owner._id === loggedUser._id);
  const showInfo = notFinished && userCanEdit;

  const tabs = showInfo ? [timelineTab, infoTab] : [timelineTab];

  const performRequest = (url, params) =>
    new Request(_csrf)
      .put(url, params)
      .done(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('success_update'),
        );
        fetchFreshLead();
      })
      .fail(err => {
        Notifications.showErrorFromRequest(err, 'error_leadUpdate');
      });

  const confirmOwner = confirmation =>
    performRequest(`/challenges/leads/${lead._id}/owner/${confirmation}`);

  const setStatus = status =>
    performRequest(`/challenges/leads/${lead._id}/status/${status}`);

  const takeLead = () =>
    performRequest(`/challenges/leads/${lead._id}/attach/owner`);

  const handleAction = action => {
    switch (action) {
      case LEAD_DETAILS_ACTION.ACCEPT_OWNER:
        confirmOwner('accept');
        break;
      case LEAD_DETAILS_ACTION.REFUSE_OWNER:
        confirmOwner('refuse');
        break;
      case LEAD_DETAILS_ACTION.WIN:
        setStatus('won');
        break;
      case LEAD_DETAILS_ACTION.LOSE:
        setStatus('lost');
        break;
      case LEAD_DETAILS_ACTION.TAKE:
      default:
        takeLead();
        break;
    }
  };

  return (
    <Title title={t('leadDetails_head_title')}>
      <ContentContainer>
        <SubHeader
          title={t('leadDetails_title')}
          breadcrumbs={[
            { link: '/challenges', text: t('header_performance_challenges') },
            {
              link: `/challenges/${lead.game}`,
              text: t('header_performance_challengeDetails'),
            },
            {
              link: `/challenges/${lead.game}/leads`,
              text: t('header_performance_challengeDetails_leads'),
            },
            { link: '', text: t('leadDetails_title') },
          ]}
        />
        <div className="m-content">
          <div className="row full-page-container-with-subheader">
            <div className="col-lg-12">
              <TabbedPortlet
                tabs={tabs}
                actions={<LeadActions lead={lead} onAction={handleAction} />}
              />
            </div>
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

LeadDetails.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  lead: PropTypes.shape(GameLeadProp.propType).isRequired,
};

export default compose(
  translate,
  withCSRF,
)(LeadDetails);
